import * as Sentry from '@sentry/react';
import type { Breadcrumb, Scope, Exception, StackFrame, ErrorEvent, EventHint } from '@sentry/react';

// Initialize Sentry with comprehensive monitoring
export const initSentry = () => {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN || 'https://8c985a52d4db1df06a107820d501b8ff@o4509458810994688.ingest.us.sentry.io/4509458821677056',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Configure session replay
        maskAllText: true,
        blockAllMedia: true,
        // Disable workers to avoid CSP issues
        useCompression: false,
      }),
      // Send console.log, console.error, and console.warn calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ["log", "error", "warn"] }),
    ],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === 'development' ? 1.0 : 0.2,
    // Session Replay
    replaysSessionSampleRate: import.meta.env.MODE === 'development' ? 1.0 : 0.1,
    replaysOnErrorSampleRate: 1.0,
    
    // Enable Debug in Development
    debug: import.meta.env.MODE === 'development',
    
    // Set environment
    environment: import.meta.env.MODE,
    
    // Disable worker-based transport to avoid CSP issues
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    
    // Configure logging experiments
    _experiments: { 
      enableLogs: true,
      beforeSendLog: (log) => {
        // Don't send debug logs in production
        if (import.meta.env.MODE === 'production' && log.level === 'debug') {
          return null;
        }
        
        // Don't send logs in development unless explicitly enabled
        if (import.meta.env.MODE === 'development' && !import.meta.env.VITE_ENABLE_SENTRY) {
          return null;
        }
        
        return log;
      },
    },
    
    // Configure beforeSend to sanitize sensitive data
    beforeSend(event: ErrorEvent, _hint: EventHint) {
      // Don't send events in development unless explicitly enabled
      if (import.meta.env.MODE === 'development' && !import.meta.env.VITE_ENABLE_SENTRY) {
        return null;
      }
      
      // Sanitize URLs in stacktraces
      if (event.exception) {
        event.exception.values?.forEach((exception: Exception) => {
          exception.stacktrace?.frames?.forEach((frame: StackFrame) => {
            if (frame.filename) {
              // Remove query strings and hashes from URLs
              frame.filename = frame.filename.split('?')[0].split('#')[0];
            }
          });
        });
      }
      
      return event;
    },
    
    // Configure beforeBreadcrumb to control what breadcrumbs are captured
    beforeBreadcrumb(breadcrumb: Breadcrumb) {
      // Don't capture console logs in production
      if (
        import.meta.env.MODE === 'production' &&
        breadcrumb.category === 'console'
      ) {
        return null;
      }
      
      return breadcrumb;
    },
  });
};

// Utility function to log errors with extra context
export const logError = (error: Error, context?: Record<string, any>) => {
  Sentry.withScope((scope: Scope) => {
    if (context) {
      // Add extra context to the error
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    
    // Add current route as tag
    scope.setTag('route', window.location.pathname);
    
    Sentry.captureException(error);
  });
};

// Utility function to set user context
export const setUserContext = (user: { id: string; email?: string; role?: string }) => {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    role: user.role,
    // Add IP address to help with debugging
    ip_address: '{{auto}}',
  });
  
  // Set user role as tag for easier filtering
  if (user.role) {
    Sentry.setTag('user_role', user.role);
  }
};

// Utility function to clear user context
export const clearUserContext = () => {
  Sentry.setUser(null);
  // Clear user-related tags
  Sentry.setTag('user_role', null);
};

// Utility function to add breadcrumb
export const addBreadcrumb = (
  message: string,
  category?: string,
  level?: Sentry.SeverityLevel,
  data?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
  });
};

// Utility function to set transaction name
export const setTransactionName = (name: string) => {
  const span = Sentry.getActiveSpan();
  if (span) {
    span.updateName(name);
  }
};

// Utility function to measure performance
export const measurePerformance = (name: string, operation: () => any) => {
  return Sentry.startSpan({
    name,
    op: 'measure',
  }, () => {
    try {
      return operation();
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  });
};

// Utility function to track API calls
export const trackApiCall = (endpoint: string, method: string) => {
  const span = Sentry.startInactiveSpan({
    name: `API ${method.toUpperCase()} ${endpoint}`,
    op: 'http.client',
  });
  
  return {
    finish: (status?: number, error?: Error) => {
      if (error) {
        span?.setStatus({ code: 2, message: 'error' });
        span?.setAttribute('error', error.message);
      } else if (status) {
        span?.setStatus({ code: status < 400 ? 1 : 2, message: status < 400 ? 'ok' : 'error' });
      }
      span?.end();
    },
  };
};

// Sentry Logger utilities
const { logger } = Sentry;

export const sentryLogger = {
  trace: (message: string, attributes?: Record<string, any>) => {
    if (attributes) {
      logger.trace(logger.fmt`${message}`, attributes);
    } else {
      logger.trace(message);
    }
  },
  
  debug: (message: string, attributes?: Record<string, any>) => {
    if (attributes) {
      logger.debug(logger.fmt`${message}`, attributes);
    } else {
      logger.debug(message);
    }
  },
  
  info: (message: string, attributes?: Record<string, any>) => {
    if (attributes) {
      logger.info(logger.fmt`${message}`, attributes);
    } else {
      logger.info(message);
    }
  },
  
  warn: (message: string, attributes?: Record<string, any>) => {
    if (attributes) {
      logger.warn(logger.fmt`${message}`, attributes);
    } else {
      logger.warn(message);
    }
  },
  
  error: (message: string, attributes?: Record<string, any>) => {
    if (attributes) {
      logger.error(logger.fmt`${message}`, attributes);
    } else {
      logger.error(message);
    }
  },
  
  fatal: (message: string, attributes?: Record<string, any>) => {
    if (attributes) {
      logger.fatal(logger.fmt`${message}`, attributes);
    } else {
      logger.fatal(message);
    }
  },

  // Convenience methods for common use cases
  logUserAction: (action: string, userId?: string, additional?: Record<string, any>) => {
    logger.info(logger.fmt`User action: ${action}`, {
      action,
      userId,
      route: window.location.pathname,
      timestamp: new Date().toISOString(),
      ...additional,
    });
  },

  logApiCall: (method: string, endpoint: string, status?: number, duration?: number, error?: Error) => {
    const attributes = {
      method: method.toUpperCase(),
      endpoint,
      status,
      duration,
      timestamp: new Date().toISOString(),
    };

    if (error) {
      logger.error(logger.fmt`API call failed: ${method.toUpperCase()} ${endpoint}`, {
        ...attributes,
        error: error.message,
        stack: error.stack,
      });
    } else if (status && status >= 400) {
      logger.warn(logger.fmt`API call returned error: ${method.toUpperCase()} ${endpoint}`, attributes);
    } else {
      logger.info(logger.fmt`API call successful: ${method.toUpperCase()} ${endpoint}`, attributes);
    }
  },

  logFormSubmission: (formName: string, success: boolean, errors?: string[], data?: Record<string, any>) => {
    const attributes = {
      formName,
      success,
      errors,
      route: window.location.pathname,
      timestamp: new Date().toISOString(),
      ...data,
    };

    if (success) {
      logger.info(logger.fmt`Form submitted successfully: ${formName}`, attributes);
    } else {
      logger.warn(logger.fmt`Form submission failed: ${formName}`, attributes);
    }
  },

  logPageView: (page: string, userId?: string, additional?: Record<string, any>) => {
    logger.info(logger.fmt`Page viewed: ${page}`, {
      page,
      userId,
      route: window.location.pathname,
      timestamp: new Date().toISOString(),
      ...additional,
    });
  },
}; 